import React, { useMemo, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconPlus, IconUserCheck, IconUserX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  useNominations,
  useUpdateNominationStatus,
  useAddNomination,
  useUsersDirectory,
} from '../../hooks/useApi';
import { useUserStore } from '../../store/userStore';
import { useAnalytics, AnalyticsEvents } from '../../analytics';
import { showToast } from '../../utils/toast';
import { Nomination, RaterRole, NominationStatus, User } from '../../types';

interface NominationListProps {
  cycleId: string;
  /** Оцениваемые цикла — группа рисуется на каждого, даже без номинаций. */
  subjects: User[];
  /** Оцениваемые, чьими номинациями управляет текущий человек (`can.nominate` с сервера). */
  nominateFor: string[];
}

const ROLE_COLORS: Record<RaterRole, string> = {
  self: 'gray',
  manager: 'blue',
  peer: 'teal',
  subordinate: 'orange',
};

const NOMINATED_BY_COLORS: Record<string, string> = {
  system: 'gray',
  self: 'teal',
  manager: 'blue',
};

const STATUS_COLORS: Record<NominationStatus, string> = {
  pending: 'yellow',
  approved: 'teal',
  rejected: 'red',
};

interface SubjectGroup {
  subjectId: string;
  subjectName: string;
  subjectAvatar?: string;
  nominations: Nomination[];
}

export const NominationList: React.FC<NominationListProps> = ({
  cycleId,
  subjects,
  nominateFor,
}) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const meId = useUserStore((s) => s.user?._id);
  const { data: nominations = [], isLoading } = useNominations(cycleId);
  // /users — админский; справочник доступен всем, иначе оцениваемому некого выбрать.
  const { data: users = [] } = useUsersDirectory();
  const updateStatus = useUpdateNominationStatus();
  const addNomination = useAddNomination();

  // Per-subject add-rater state
  const [addRaterState, setAddRaterState] = useState<
    Record<string, { userId: string | null; role: string | null }>
  >({});

  // Group nominations by subject
  const groups: SubjectGroup[] = useMemo(() => {
    const map = new Map<string, SubjectGroup>();
    for (const s of subjects) {
      map.set(s._id, {
        subjectId: s._id,
        subjectName: s.name,
        subjectAvatar: s.avatar || undefined,
        nominations: [],
      });
    }
    for (const nom of nominations) {
      const sid = typeof nom.subjectId === 'object' ? nom.subjectId._id : nom.subjectId;
      const sName = typeof nom.subjectId === 'object' ? nom.subjectId.name : sid;
      const sAvatar = typeof nom.subjectId === 'object' ? nom.subjectId.avatar : undefined;

      if (!map.has(sid)) {
        map.set(sid, {
          subjectId: sid,
          subjectName: sName,
          subjectAvatar: sAvatar,
          nominations: [],
        });
      }
      map.get(sid)!.nominations.push(nom);
    }
    // Чужие номинации видят только те, кто может ими управлять.
    return Array.from(map.values()).filter(
      (g) => nominateFor.includes(g.subjectId) || g.subjectId === meId,
    );
  }, [nominations, subjects, nominateFor, meId]);

  const handleApprove = async (nomId: string) => {
    try {
      await updateStatus.mutateAsync({ cycleId, nomId, status: 'approved' });
      track(AnalyticsEvents.REVIEW_360_NOMINATION_APPROVED, { cycleId, nomId });
      showToast({
        title: t('common.success'),
        message: t('review360.nominationApproved'),
        color: 'teal',
      });
    } catch {
      showToast({
        title: t('common.error'),
        message: t('review360.nominationError'),
        color: 'red',
      });
    }
  };

  const handleReject = async (nomId: string) => {
    try {
      await updateStatus.mutateAsync({ cycleId, nomId, status: 'rejected' });
      showToast({
        title: t('common.success'),
        message: t('review360.nominationRejected'),
        color: 'yellow',
      });
    } catch {
      showToast({
        title: t('common.error'),
        message: t('review360.nominationError'),
        color: 'red',
      });
    }
  };

  const handleApproveAll = async (subjectNominations: Nomination[]) => {
    const pending = subjectNominations.filter((n) => n.status === 'pending');
    let failed = 0;
    for (const nom of pending) {
      try {
        await updateStatus.mutateAsync({ cycleId, nomId: nom._id, status: 'approved' });
        track(AnalyticsEvents.REVIEW_360_NOMINATION_APPROVED, { cycleId, nomId: nom._id });
      } catch {
        failed += 1; // continue with others
      }
    }
    showToast(
      failed > 0
        ? { title: t('common.error'), message: t('review360.nominationError'), color: 'red' }
        : { title: t('common.success'), message: t('review360.nominationApproved'), color: 'teal' },
    );
  };

  const handleAddRater = async (subjectId: string) => {
    const state = addRaterState[subjectId];
    if (!state?.userId || !state?.role) return;

    try {
      await addNomination.mutateAsync({
        cycleId,
        subjectId,
        raterId: state.userId,
        raterRole: state.role,
      });
      showToast({
        title: t('common.success'),
        message: t('review360.raterAdded'),
        color: 'teal',
      });
      setAddRaterState((prev) => ({
        ...prev,
        [subjectId]: { userId: null, role: null },
      }));
    } catch {
      showToast({
        title: t('common.error'),
        message: t('review360.raterAddError'),
        color: 'red',
      });
    }
  };

  const usersData = useMemo(() => users.map((u) => ({ value: u._id, label: u.name })), [users]);

  const roleOptions = useMemo(
    () =>
      (['peer', 'subordinate'] as RaterRole[]).map((r) => ({
        value: r,
        label: t(`review360.roles.${r}`),
      })),
    [t],
  );

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  if (groups.length === 0) {
    return (
      <Text ta="center" c="dimmed" py="xl">
        {t('review360.nominations')}
      </Text>
    );
  }

  return (
    <Accordion variant="separated" radius="md">
      {groups.map((group) => {
        const approvedCount = group.nominations.filter((n) => n.status === 'approved').length;
        const pendingCount = group.nominations.filter((n) => n.status === 'pending').length;
        const raterState = addRaterState[group.subjectId] ?? { userId: null, role: null };
        // Подтверждать и отклонять — тот, кому сервер дал управлять номинациями оцениваемого.
        const canApprove = nominateFor.includes(group.subjectId);

        return (
          <Accordion.Item key={group.subjectId} value={group.subjectId}>
            <Accordion.Control>
              <Group gap="sm">
                <Avatar src={group.subjectAvatar} radius="xl" size="sm">
                  {group.subjectName?.charAt(0)}
                </Avatar>
                <Text fw={500}>{group.subjectName}</Text>
                <Badge color="teal" variant="light" size="sm">
                  {t('review360.approvedCount', { count: approvedCount })}
                </Badge>
                {pendingCount > 0 && (
                  <Badge color="yellow" variant="light" size="sm">
                    {t('review360.pendingCount', { count: pendingCount })}
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {/* Approve all button */}
                {canApprove && pendingCount > 0 && (
                  <Group justify="flex-end">
                    <Button
                      variant="light"
                      color="teal"
                      size="xs"
                      leftSection={<IconCheck size={14} />}
                      onClick={() => handleApproveAll(group.nominations)}
                      loading={updateStatus.isPending}
                    >
                      {t('review360.approveAll')}
                    </Button>
                  </Group>
                )}

                {/* Nominations table */}
                <Table.ScrollContainer minWidth={600}>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('review360.raterName')}</Table.Th>
                        <Table.Th>{t('common.role')}</Table.Th>
                        <Table.Th>{t('review360.nominatedByLabel')}</Table.Th>
                        <Table.Th>{t('common.status')}</Table.Th>
                        <Table.Th>{t('common.actions')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {group.nominations.map((nom) => {
                        const rater = typeof nom.raterId === 'object' ? nom.raterId : null;
                        return (
                          <Table.Tr key={nom._id}>
                            <Table.Td>
                              <Group gap="xs" wrap="nowrap">
                                <Avatar src={rater?.avatar} radius="xl" size="sm">
                                  {rater?.name?.charAt(0) ?? '?'}
                                </Avatar>
                                <Text size="sm" truncate>
                                  {rater?.name ?? '—'}
                                </Text>
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Badge color={ROLE_COLORS[nom.raterRole]} variant="light" size="sm">
                                {t(`review360.roles.${nom.raterRole}`)}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                color={NOMINATED_BY_COLORS[nom.nominatedBy] ?? 'gray'}
                                variant="light"
                                size="sm"
                              >
                                {t(`review360.nominatedBy.${nom.nominatedBy}`)}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge color={STATUS_COLORS[nom.status]} variant="light" size="sm">
                                {t(`review360.${nom.status}`)}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {canApprove && nom.status === 'pending' && (
                                <Group gap={4}>
                                  <Tooltip label={t('review360.approved')}>
                                    <ActionIcon
                                      variant="light"
                                      color="teal"
                                      size="sm"
                                      onClick={() => handleApprove(nom._id)}
                                      loading={updateStatus.isPending}
                                    >
                                      <IconUserCheck size={14} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label={t('review360.rejected')}>
                                    <ActionIcon
                                      variant="light"
                                      color="red"
                                      size="sm"
                                      onClick={() => handleReject(nom._id)}
                                      loading={updateStatus.isPending}
                                    >
                                      <IconUserX size={14} />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>

                {/* Add rater section */}
                <Group gap="sm" align="flex-end">
                  <Select
                    placeholder={t('review360.selectUser')}
                    data={usersData}
                    value={raterState.userId}
                    onChange={(val) =>
                      setAddRaterState((prev) => ({
                        ...prev,
                        [group.subjectId]: { ...raterState, userId: val },
                      }))
                    }
                    searchable
                    style={{ flex: 1 }}
                  />
                  <Select
                    placeholder={t('review360.selectRole')}
                    data={roleOptions}
                    value={raterState.role}
                    onChange={(val) =>
                      setAddRaterState((prev) => ({
                        ...prev,
                        [group.subjectId]: { ...raterState, role: val },
                      }))
                    }
                    style={{ minWidth: 160 }}
                  />
                  <Button
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => handleAddRater(group.subjectId)}
                    loading={addNomination.isPending}
                    disabled={!raterState.userId || !raterState.role}
                  >
                    {t('review360.addRater')}
                  </Button>
                </Group>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};
